/**
 * TanStack Query (React Query) client.
 *
 * Handles all server state: caching, refetching, loading/error states.
 * Components use `useQuery` for reads and `useMutation` for writes.
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't refetch on window focus (annoying for mobile PWA)
      refetchOnWindowFocus: false,
      // ALWAYS refetch a query when its component mounts — i.e. whenever the
      // user navigates to a page, that page's data is pulled fresh, even if a
      // cached copy is still within `staleTime`. This is what makes navigating
      // to a page reflect changes made elsewhere (e.g. the clinic-ticket
      // dropdown / clinic history updating after you enter or change a clinic)
      // WITHOUT needing a logout/login to clear the cache.
      //
      // UX note: a cached copy still renders instantly while the refetch runs
      // in the background (`isFetching`), so pages don't flash a blank/skeleton
      // on revisit — they show last-known data, then update in place. The
      // server remains the source of truth (e.g. ticket eligibility is
      // re-validated on submit), so a briefly-stale list can't cause a bad write.
      refetchOnMount: 'always',
      // Retry once on failure (network blips)
      retry: 1,
      // `staleTime` still dedupes rapid refetches and governs background
      // freshness between mounts; `refetchOnMount: 'always'` overrides it for
      // the on-navigation refetch above.
      staleTime: 30_000,
      // Cache persists for 5 minutes after last component unmount
      gcTime: 5 * 60_000,
    },
    mutations: {
      retry: 0, // Don't auto-retry mutations (e.g., payments) — let user retry
    },
  },
});
